import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Badge } from './Badge';
import { formatDate } from '../../utils/dateUtils';
import type { ReleaseNote, ReleaseNoteItem, ReleaseNoteItemType } from '../../data/releaseNotes';

const TYPE_VARIANT: Record<ReleaseNoteItemType, 'success' | 'info' | 'warning'> = {
  feature: 'success',
  improvement: 'info',
  fix: 'warning',
};

interface ReleaseNoteEntryProps {
  note: ReleaseNote;
  /** Blendet Version und Datum aus — im Dialog steht beides bereits im Untertitel. */
  hideMeta?: boolean;
}

/**
 * Ein Release-Eintrag. Die Texte kommen aus src/data/releaseNotes.json und werden
 * bewusst nicht übersetzt — nur der Rahmen (Typ-Labels, Datum) folgt der UI-Sprache.
 */
export function ReleaseNoteEntry({ note, hideMeta = false }: ReleaseNoteEntryProps) {
  const { t } = useTranslation('releaseNotes');

  return (
    <div className="space-y-3">
      {!hideMeta && (
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[15px] font-semibold text-slate-900 tracking-tight">
            {note.title}
          </span>
          <span className="text-[11px] font-mono tabular-nums text-slate-500">
            {t('meta', { version: note.version, date: formatDate(note.date) })}
          </span>
        </div>
      )}

      <ul className="space-y-2">
        {note.items.map((item, index) => (
          <ReleaseNoteItemRow
            key={`${note.version}-${index}`}
            item={item}
            detailsId={`release-note-${note.version}-${index}`}
          />
        ))}
      </ul>
    </div>
  );
}

interface ReleaseNoteItemRowProps {
  item: ReleaseNoteItem;
  detailsId: string;
}

/**
 * Ein Punkt der Release Notes. Hat er `details`, lässt sich unter dem Kurztext eine
 * Erklärung („wo finde ich das, wie bediene ich es“) auf- und zuklappen.
 */
function ReleaseNoteItemRow({ item, detailsId }: ReleaseNoteItemRowProps) {
  const { t } = useTranslation('releaseNotes');
  const [expanded, setExpanded] = useState(false);
  const details = item.details ?? [];
  const hasDetails = details.length > 0;

  return (
    <li className="flex items-start gap-2">
      <Badge variant={TYPE_VARIANT[item.type]} className="mt-0.5 flex-shrink-0">
        {t(`types.${item.type}`)}
      </Badge>
      <div className="min-w-0 flex-1">
        <span className="text-[13px] text-slate-700">{item.text}</span>
        {hasDetails && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              aria-controls={detailsId}
              className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-[#00A871] hover:underline"
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
              {expanded ? t('item.hideDetails') : t('item.showDetails')}
            </button>
            {expanded && (
              <div
                id={detailsId}
                className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                {details.map((paragraph, index) => (
                  <p key={index} className="text-[13px] leading-relaxed text-slate-600">
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </li>
  );
}

export default ReleaseNoteEntry;
